import React from 'react';
import { cn } from '@/lib/utils';
import { Heart, Users } from 'lucide-react';

/**
 * CoRegulate Indicators & Badges
 * 
 * Status indicators and badges with brand styling
 */

/**
 * Safe Indicator
 * Shows "safe space" or grounding status
 */
export const SafeIndicator = ({
  children,
  className,
  variant = 'sage', // 'sage' | 'active'
  ...props
}) => {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2',
        'px-3 py-1.5 rounded-full',
        'text-sm font-medium',
        variant === 'sage'
          ? 'bg-[rgba(52,70,72,0.12)] text-[#1D6B6B]'
          : 'bg-[#7D8E95] text-[#1D6B6B]',
        className
      )}
      {...props}
    >
      <span className={cn(
        'w-2 h-2 rounded-full',
        variant === 'sage' ? 'bg-[#7D8E95]' : 'bg-[#1D6B6B]/30',
        variant === 'active' && 'animate-pulse'
      )} />
      {children || 'Safe Space'}
    </div>
  );
};
SafeIndicator.displayName = 'SafeIndicator';

/**
 * Co-Regulate Indicator
 * Shows partnership/co-regulation mode
 */
export const CoRegulateIndicator = ({
  children,
  className,
  active = false,
  ...props
}) => {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2',
        'px-3 py-1.5 rounded-full',
        'text-sm font-medium',
        active
          ? 'bg-[#FFBB98] text-[#1D6B6B]'
          : 'bg-[rgba(255,187,152,0.15)] text-[#FFBB98]',
        className
      )}
      {...props}
    >
      <Users size={14} strokeWidth={1.5} />
      {children || 'Co-Regulating'}
    </div>
  );
};
CoRegulateIndicator.displayName = 'CoRegulateIndicator';

/**
 * Generic Badge
 * Flexible badge component
 */
export const Badge = ({
  children,
  className,
  variant = 'neutral', // 'neutral' | 'sage' | 'rose' | 'lavender' | 'blue' | 'gold'
  size = 'md', // 'sm' | 'md' | 'lg'
  icon,
  ...props
}) => {
  const variantStyles = {
    neutral: 'bg-[#FFBB98] text-[#2A8585]',
    sage: 'bg-[rgba(52,70,72,0.15)] text-[#1D6B6B]',
    eucalyptus: 'bg-[rgba(125,142,149,0.15)] text-[#1D6B6B]',
    rose: 'bg-[rgba(255,187,152,0.15)] text-[#FFBB98]',
    lavender: 'bg-[rgba(125,142,149,0.15)] text-[#7D8E95]',
    blue: 'bg-[rgba(125,142,149,0.2)] text-[#7D8E95]',
    gold: 'bg-[rgba(255,187,152,0.2)] text-[#FFBB98]',
  };

  const sizeStyles = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-sm gap-1.5',
    lg: 'px-3 py-1.5 text-sm gap-2',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
      {...props}
    >
      {icon}
      {children}
    </span>
  );
};
Badge.displayName = 'Badge';

/**
 * Status Badge
 * Shows status with optional dot indicator
 */
export const StatusBadge = ({
  children,
  className,
  status = 'default', // 'default' | 'active' | 'success' | 'warning' | 'gentle'
  showDot = true,
  ...props
}) => {
  const statusStyles = {
    default: {
      badge: 'bg-[#FFBB98] text-[#2A8585]',
      dot: 'bg-[#2A8585]',
    },
    active: {
      badge: 'bg-[rgba(52,70,72,0.15)] text-[#1D6B6B]',
      dot: 'bg-[#7D8E95]',
    },
    success: {
      badge: 'bg-[rgba(125,142,149,0.15)] text-[#1D6B6B]',
      dot: 'bg-[#7D8E95]',
    },
    warning: {
      badge: 'bg-[rgba(255,187,152,0.2)] text-[#FFBB98]',
      dot: 'bg-[#FFBB98]',
    },
    gentle: {
      badge: 'bg-[rgba(255,187,152,0.15)] text-[#FFBB98]',
      dot: 'bg-[#FFBB98]',
    },
  };

  const s = statusStyles[status];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2',
        'px-2.5 py-1 rounded-full',
        'text-sm font-medium',
        s.badge,
        className
      )}
      {...props}
    >
      {showDot && (
        <span className={cn(
          'w-2 h-2 rounded-full',
          s.dot,
          status === 'active' && 'animate-pulse'
        )} />
      )}
      {children}
    </span>
  );
};
StatusBadge.displayName = 'StatusBadge';

/**
 * Counter Badge
 * Small numerical indicator
 */
export const CounterBadge = ({
  count = 0,
  className,
  max = 99,
  variant = 'sage',
  ...props
}) => {
  const displayCount = count > max ? `${max}+` : count;

  const variantStyles = {
    sage: 'bg-[#7D8E95] text-[#1D6B6B]',
    rose: 'bg-[#FFBB98] text-[#1D6B6B]',
    neutral: 'bg-[#2A8585] text-white',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        'min-w-[20px] h-5 px-1.5 rounded-full',
        'text-xs font-semibold',
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {displayCount}
    </span>
  );
};
CounterBadge.displayName = 'CounterBadge';

/**
 * Feeling Badge
 * For displaying emotional states
 */
export const FeelingBadge = ({
  children,
  className,
  feeling, // 'calm' | 'anxious' | 'overwhelmed' | 'grounded' | 'connected'
  ...props
}) => {
  const feelingStyles = {
    calm: {
      bg: 'bg-[rgba(125,142,149,0.2)]',
      text: 'text-[#7D8E95]',
      icon: '🌊',
    },
    anxious: {
      bg: 'bg-[rgba(255,187,152,0.2)]',
      text: 'text-[#FFBB98]',
      icon: '🌀',
    },
    overwhelmed: {
      bg: 'bg-[rgba(255,187,152,0.15)]',
      text: 'text-[#FFBB98]',
      icon: '🌋',
    },
    grounded: {
      bg: 'bg-[rgba(52,70,72,0.15)]',
      text: 'text-[#1D6B6B]',
      icon: '🌿',
    },
    connected: {
      bg: 'bg-[rgba(125,142,149,0.15)]',
      text: 'text-[#7D8E95]',
      icon: '💜',
    },
  };

  const f = feelingStyles[feeling] || feelingStyles.calm;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2',
        'px-3 py-1.5 rounded-full',
        'text-sm font-medium',
        f.bg,
        f.text,
        className
      )}
      {...props}
    >
      <span>{f.icon}</span>
      {children || feeling}
    </span>
  );
};
FeelingBadge.displayName = 'FeelingBadge';

/**
 * Session Mode Indicator
 * Shows current regulation mode
 */
export const SessionModeIndicator = ({
  mode = 'individual', // 'individual' | 'partner' | 'shared'
  className,
  ...props
}) => {
  const modeStyles = {
    individual: {
      bg: 'bg-[rgba(52,70,72,0.12)]',
      text: 'text-[#1D6B6B]',
      label: 'Individual',
      icon: <Heart size={14} strokeWidth={1.5} />,
    },
    partner: {
      bg: 'bg-[rgba(255,187,152,0.15)]',
      text: 'text-[#FFBB98]',
      label: 'Partner',
      icon: <Users size={14} strokeWidth={1.5} />,
    },
    shared: {
      bg: 'bg-[rgba(125,142,149,0.15)]',
      text: 'text-[#7D8E95]',
      label: 'Shared',
      icon: <Users size={14} strokeWidth={1.5} />,
    },
  };

  const m = modeStyles[mode];

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2',
        'px-3 py-1.5 rounded-full',
        'text-sm font-medium',
        m.bg,
        m.text,
        className
      )}
      {...props}
    >
      {m.icon}
      {m.label}
    </div>
  );
};
SessionModeIndicator.displayName = 'SessionModeIndicator';

export default Badge;
